import React, { useState, useEffect } from 'react';

const posters = [
  '/images/ad-poster-1.jpg',
  '/images/ad-poster-2.jpg',
  '/images/ad-poster-3.jpg',
  '/images/ad-poster-4.jpg'
];

const AdPosterSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Auto-play logic for poster slider
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % posters.length);
    }, 3500); 
    return () => clearInterval(interval);
  }, []);

  const scrollToOrder = () => {
    document.getElementById("order").scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="bg-[#FAF9F6] py-10 md:py-20 relative overflow-hidden">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        
        {/* --- Part 1: Slider Gallery --- */}
        <div className="mb-12 md:mb-20">
          <div className="text-center mb-6 md:mb-10">
            <span className="inline-block bg-orange-100 text-orange-700 px-4 py-1.5 rounded-full text-[11px] sm:text-xs md:text-sm font-extrabold tracking-widest uppercase mb-3 md:mb-4 shadow-sm border border-orange-200/50">
              Special Offers
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 tracking-tight leading-tight px-2">
              আমাদের স্পেশাল অফার গ্যালারি
            </h2>
          </div>

          <div className="relative max-w-4xl mx-auto">
            <div className="overflow-hidden rounded-2xl md:rounded-[2rem] shadow-[0_15px_40px_rgb(0,0,0,0.06)] bg-white ring-1 ring-gray-900/5">
              <div 
                className="flex transition-transform duration-700 ease-[cubic-bezier(0.25,1,0.5,1)]"
                style={{ transform: `translateX(-${currentIndex * 100}%)` }}
              >
                {posters.map((poster, idx) => (
                  <div key={idx} className="w-full shrink-0 flex justify-center items-center p-2 sm:p-3 md:p-6 bg-gradient-to-b from-gray-50 to-gray-100/50">
                    <img 
                      src={poster} 
                      alt={`Special Offer ${idx + 1}`} 
                      className="w-full h-auto max-h-[300px] sm:max-h-[400px] md:max-h-[500px] lg:max-h-[600px] object-contain rounded-xl sm:rounded-2xl shadow-sm"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Dots */}
            <div className="flex justify-center items-center gap-2.5 mt-5 md:mt-8">
              {posters.map((_, idx) => (
                <button 
                  key={idx}
                  onClick={() => setCurrentIndex(idx)}
                  className={`transition-all duration-500 rounded-full ${
                    currentIndex === idx ? 'w-8 h-2 bg-orange-500 shadow-md' : 'w-2 h-2 bg-gray-300 hover:bg-gray-400'
                  }`}
                  aria-label={`Go to slide ${idx + 1}`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* --- Part 2: Mega Promo / Urgency Section --- */}
        <div className="max-w-4xl mx-auto mb-12 md:mb-20">
          {/* Animated gradient border wrapper */}
          <div 
            className="rounded-[1.75rem] md:rounded-[2.5rem] p-[3px] md:p-[4px] relative"
            style={{
              background: 'linear-gradient(135deg, #ef4444, #f97316, #eab308, #ef4444, #f97316)',
              backgroundSize: '300% 300%',
              animation: 'gradient-shift 4s ease infinite'
            }}
          >
            {/* Outer glow effect */}
            <div 
              className="absolute -inset-1 rounded-[2rem] md:rounded-[3rem] blur-md opacity-60 -z-10"
              style={{
                background: 'linear-gradient(135deg, #ef4444, #f97316, #eab308)',
                backgroundSize: '300% 300%',
                animation: 'gradient-shift 4s ease infinite'
              }}
            ></div>

            <div className="bg-gradient-to-br from-[#1a0a0a] via-[#1c0d0d] to-[#0f0505] rounded-[1.6rem] md:rounded-[2.4rem] p-6 sm:p-8 md:p-12 lg:p-16 text-center relative overflow-hidden">
              
              {/* Animated background particles / bokeh */}
              <div className="absolute top-6 left-8 w-32 h-32 md:w-52 md:h-52 rounded-full bg-red-500 blur-[100px] opacity-20" style={{animation: 'float 6s ease-in-out infinite'}}></div>
              <div className="absolute bottom-6 right-8 w-40 h-40 md:w-60 md:h-60 rounded-full bg-orange-500 blur-[100px] opacity-15" style={{animation: 'float 8s ease-in-out infinite 1s'}}></div>
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 md:w-72 md:h-72 rounded-full bg-amber-500 blur-[120px] opacity-10"></div>
              
              {/* Sparkle decorations */}
              <div className="absolute top-4 right-[15%] text-yellow-400 text-lg md:text-2xl" style={{animation: 'sparkle 2s ease-in-out infinite'}}>✦</div>
              <div className="absolute top-[20%] left-[10%] text-orange-400 text-sm md:text-lg" style={{animation: 'sparkle 2.5s ease-in-out infinite 0.5s'}}>✦</div>
              <div className="absolute bottom-[15%] right-[8%] text-red-400 text-base md:text-xl" style={{animation: 'sparkle 3s ease-in-out infinite 1s'}}>✦</div>
              <div className="absolute bottom-[25%] left-[20%] text-amber-300 text-xs md:text-base" style={{animation: 'sparkle 2.8s ease-in-out infinite 0.8s'}}>✦</div>

              <div className="relative z-10">
                {/* Urgency Badge */}
                <div className="inline-flex items-center gap-2 bg-red-500/20 backdrop-blur-sm border border-red-500/40 text-red-300 px-5 py-2 md:px-6 md:py-2.5 rounded-full text-[12px] md:text-sm font-extrabold tracking-widest uppercase mb-5 md:mb-7" style={{animation: 'glow-pulse 2s ease-in-out infinite'}}>
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
                  </span>
                  🔥 সীমিত সময়ের অফার 🔥
                </div>
                
                {/* Floating Gift */}
                <div className="text-4xl sm:text-5xl md:text-6xl mb-4 md:mb-6" style={{animation: 'float 3s ease-in-out infinite'}}>
                  🎁
                </div>

                {/* Main Heading */}
                <h3 className="text-[24px] sm:text-3xl md:text-5xl font-black text-white mb-3 md:mb-5 leading-tight">
                  ২ কেজি অর্ডার করলেই{' '}
                  <span 
                    className="inline-block mt-1 sm:mt-0 text-transparent bg-clip-text"
                    style={{
                      backgroundImage: 'linear-gradient(90deg, #fbbf24, #f97316, #ef4444, #fbbf24)',
                      backgroundSize: '200% auto',
                      animation: 'text-shimmer 3s linear infinite',
                      WebkitBackgroundClip: 'text',
                    }}
                  >
                    বিশেষ উপহার!
                  </span>
                </h3>

                {/* Decorative divider */}
                <div className="flex items-center justify-center gap-3 mb-5 md:mb-7">
                  <div className="h-px w-12 md:w-20 bg-gradient-to-r from-transparent to-orange-500/60"></div>
                  <span className="text-orange-400 text-lg">★</span>
                  <div className="h-px w-12 md:w-20 bg-gradient-to-l from-transparent to-orange-500/60"></div>
                </div>
                
                <p className="text-gray-300 text-[14px] sm:text-base md:text-xl mb-8 md:mb-10 max-w-2xl mx-auto font-medium leading-relaxed px-2">
                  স্টক ফুরিয়ে যাওয়ার আগেই আপনার সোনামণির জন্য <span className="text-orange-300 font-bold">স্বাস্থ্যকর খাবার</span> নিশ্চিত করুন এবং জিতে নিন <span className="text-amber-300 font-bold">দারুণ সব পুরস্কার।</span>
                </p>

                {/* Stock Warning */}
                <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-full px-4 py-2 mb-7 md:mb-9">
                  <span className="text-amber-400 text-sm md:text-base font-bold">⚠️ স্টক সীমিত — দ্রুত অর্ডার করুন!</span>
                </div>
                
                {/* CTA Button */}
                <div>
                  <button
                    onClick={scrollToOrder}
                    className="relative inline-flex items-center justify-center gap-2.5 px-8 py-4 md:px-10 md:py-5 overflow-hidden rounded-full font-black text-lg md:text-xl text-white transition-all duration-300 hover:-translate-y-1 max-w-md w-[90%] sm:w-auto mx-auto"
                    style={{
                      background: 'linear-gradient(135deg, #ef4444, #f97316, #ef4444)',
                      backgroundSize: '200% 200%',
                      animation: 'gradient-shift 3s ease infinite, scale-bounce 2s ease-in-out infinite',
                      boxShadow: '0 10px 40px rgba(239, 68, 68, 0.5), 0 0 60px rgba(249, 115, 22, 0.3)'
                    }}
                  >
                    {/* Animated shine */}
                    <div className="absolute inset-0 overflow-hidden rounded-full">
                      <div 
                        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent w-full skew-x-[-20deg]"
                        style={{animation: 'shimmer 2s ease-in-out infinite'}}
                      ></div>
                    </div>
                    {/* Top glare */}
                    <div className="absolute top-0 inset-x-0 h-1/2 bg-gradient-to-b from-white/25 to-transparent rounded-t-full pointer-events-none"></div>
                    
                    <span className="relative tracking-wide text-[16px] md:text-lg whitespace-nowrap drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]">🛒 অর্ডার করুন এখন</span>
                    <svg xmlns="http://www.w3.org/2000/svg" className="relative h-5 w-5 md:h-6 md:w-6 transition-transform duration-300 group-hover:translate-x-1 drop-shadow-md" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* --- Part 3: Contact/Support Awesome Visual --- */}
        <div className="max-w-3xl mx-auto">
          <div className="relative rounded-[1.5rem] md:rounded-[2.5rem] overflow-hidden shadow-2xl p-7 sm:p-10 md:p-14 text-center text-white isolate bg-[#092212]">
            {/* Background design Elements */}
            <div className="absolute top-0 right-0 -mr-16 -mt-16 w-64 h-64 md:w-80 md:h-80 rounded-full border border-white/5 opacity-40 mix-blend-overlay"></div>
            <div className="absolute top-5 right-5 md:top-10 md:right-10 w-24 h-24 md:w-40 md:h-40 rounded-full bg-emerald-500 blur-[80px] opacity-30"></div>
            <div className="absolute bottom-0 left-0 -ml-16 -mb-16 w-56 h-56 md:w-72 md:h-72 rounded-full border border-white/5 opacity-40 mix-blend-overlay"></div>
            <div className="absolute bottom-5 left-5 md:bottom-10 md:left-10 w-24 h-24 md:w-40 md:h-40 rounded-full bg-green-500 blur-[80px] opacity-20"></div>
            
            <div className="relative z-10 w-full flex flex-col items-center">
              <p className="text-green-200 text-[11px] md:text-sm font-bold tracking-[0.15em] md:tracking-[0.2em] uppercase mb-2 md:mb-4">
                সরাসরি কথা বলুন
              </p>
              <h4 className="text-[17px] sm:text-xl md:text-2xl font-extrabold mb-5 md:mb-8 text-white/95 leading-snug">
                অর্ডার করতে বা যেকোনো প্রয়োজনে কল করুন
              </h4>
              
              <a 
                href="tel:+8801814938247" 
                className="inline-block text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-transparent bg-clip-text bg-gradient-to-r from-green-300 via-emerald-100 to-green-300 mb-6 md:mb-8 hover:scale-[1.02] transition-transform duration-300 filter drop-shadow tracking-tight"
              >
                01814938247
              </a>

              <div className="flex items-center justify-center gap-3 md:gap-5 mb-6 md:mb-8 w-[80%] mx-auto opacity-70">
                <div className="h-px w-10 md:w-16 bg-gradient-to-r from-transparent to-green-500"></div>
                <span className="text-green-100 text-[12px] md:text-sm font-medium tracking-wider">অথবা</span>
                <div className="h-px w-10 md:w-16 bg-gradient-to-l from-transparent to-green-500"></div>
              </div>

              <a
                href="https://wa.me/8801814938247"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex flex-row items-center justify-center gap-2 sm:gap-3 px-4 py-3 sm:px-8 sm:py-4 md:px-10 md:py-4 bg-[#25D366] hover:bg-[#1faB51] text-white rounded-full font-extrabold text-[14px] min-[375px]:text-[16px] sm:text-lg md:text-xl transition-all duration-300 shadow-[0_8px_25px_rgba(37,211,102,0.4)] hover:shadow-[0_12px_35px_rgba(37,211,102,0.6)] hover:-translate-y-1 ring-2 sm:ring-4 ring-[#25D366]/20 w-fit mx-auto"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 min-[375px]:w-7 min-[375px]:h-7 sm:w-9 sm:h-9 md:w-10 md:h-10 fill-current drop-shadow-sm shrink-0" viewBox="0 0 24 24">
                  <path d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.38 2.27 1.019 3.287l-.582 2.128 2.182-.573c.978.58 1.911.928 3.145.929 3.178 0 5.767-2.587 5.768-5.766.001-3.187-2.575-5.77-5.764-5.771zm3.392 8.244c-.144.405-.837.774-1.17.824-.299.045-.677.063-1.092-.069-.252-.08-.575-.187-.988-.365-1.739-.751-2.874-2.502-2.961-2.617-.087-.116-.708-.94-.708-1.793s.448-1.273.607-1.446c.159-.173.346-.217.462-.217l.332.006c.106.005.249-.04.39.298.144.347.491 1.2.534 1.287.043.087.072.188.014.304-.058.116-.087.188-.173.289l-.26.304c-.087.086-.177.18-.076.354.101.174.449.741.964 1.201.662.591 1.221.774 1.394.86s.274.072.376-.043c.101-.116.433-.506.549-.68.116-.173.231-.145.39-.087s1.011.477 1.184.564.289.13.332.202c.045.072.045.418-.097.824z" fillRule="evenodd" clipRule="evenodd"/>
                </svg>
                <span className="whitespace-nowrap">WhatsApp -এ মেসেজ দিন</span>
              </a>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default AdPosterSection;
